import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { MatTableDataSource } from '@angular/material/table';
import { NotificationService } from '@app/services/notification.service';
import { SnackbarService, TYPE_ALERT } from '@app/services/snackbar.service';
import {
    AssignmentGroupConfig,
    assignmentGroupConfigPeriod,
    AssignmentGroupConfigScope,
    AssignmentGroupConfigScopePriority,
    assignmentGroupConfigTypeShort,
    assignmentTypes as assignmentTypesObject,
} from '@data/models/assignment-group-config';
import { AssignmentGroupConfigService } from '@data/services/assignment-group-config.service';
import { TicketService } from '@data/services/ticket.service';
import { Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AssignmentGroup } from '@data/models/assignment-group.model';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { UserSessionService } from '@app/services/user-session.service';
import { Level } from '@data/models/role.model';


const FIRST_LINE_ASSIGNMENT_TYPE = 'FIRST_LINE';

type RowAssignmentGroup = Pick<AssignmentGroup, 'id' | 'name'> & Partial<Pick<AssignmentGroup, 'source'>>;

type AggregatedConfigsRow = Pick<AssignmentGroupConfig, 'assignmentType' | 'ticketType' | 'scope' | 'id'> & {
    edit: boolean;
    hover: boolean;
    isEditable: boolean;
    priority: number;
    assignmentGroupObjectHO?: RowAssignmentGroup;
    assignmentGroupObjectHNO?: RowAssignmentGroup;
    assignmentGroupHO?: string;
    assignmentGroupHNO?: string;
    assignmentGroupConfigHOId?: string;
    assignmentGroupConfigHNOId?: string;
};
@Component({
    selector: 'syn-assignment-group-config',
    templateUrl: 'assignment-group-config.component.html',
    styleUrls: ['./assignment-group-config.component.scss'],
})
export class AssignmentGroupConfigComponent implements OnInit, OnChanges {

    @Input() assignmentGroupConfigs: AssignmentGroupConfig[];
    @Input() scope: AssignmentGroupConfigScope;
    @Input() entity: string;
    @Input() entityOwners?: string[];
    @Input() displayedColumns = ['scope', 'ticketType', 'assignmentType', 'assignmentGroupHO', 'assignmentGroupHNO', 'actions'];

    public dataSource: MatTableDataSource<AggregatedConfigsRow>;
    public formColumns: string[];
    public form: FormGroup;
    public assignmentGroupHO$: Observable<AssignmentGroup[]>;
    public assignmentGroupLoaderHO = new Subject<{ term: string; strict: boolean }>();
    public assignmentGroupHNO$: Observable<AssignmentGroup[]>;
    public assignmentGroupLoaderHNO = new Subject<{ term: string; strict: boolean }>();
    public assignmentGroupHOFooter$: Observable<AssignmentGroup[]>;
    public assignmentGroupLoaderHOFooter = new Subject<{ term: string; strict: boolean }>();
    public assignmentGroupHNOFooter$: Observable<AssignmentGroup[]>;
    public assignmentGroupLoaderHNOFooter = new Subject<{ term: string; strict: boolean }>();
    public assignmentGroupConfigTypeShort = assignmentGroupConfigTypeShort;
    public assignmentGroupConfigPeriod = assignmentGroupConfigPeriod;
    public assignmentTypesObject = assignmentTypesObject;
    public assignmentTypes = Object.entries(assignmentTypesObject).map(([value, text]) => ({ text, value }));
    public assignmentGroupConfigTicketType = Object.entries(assignmentGroupConfigTypeShort).map(([value, text]) => ({ text, value }));
    public tradAssignmentGroupConfigScope = { CELL: 'Cellule', CUSTOMER: 'Client', PROJECT: 'Projet' };
    public canEdit = false;
    private editingRow: AggregatedConfigsRow;

    constructor(
        private readonly ticketService: TicketService,
        private readonly assignmentGroupConfigService: AssignmentGroupConfigService,
        private readonly formBuilder: FormBuilder,
        private readonly notificationService: NotificationService,
        private readonly snackbarService: SnackbarService,
        private readonly matDialog: MatDialog,
        private readonly userSessionService: UserSessionService,
    ) { }

    public async ngOnInit(): Promise<void> {
        this.createForm();
        this.formColumns = this.displayedColumns.map(column => `${column}Form`);
        const listAssignmentGroupsByTerm = ({ term }: { term: string; strict?: boolean }) => {
            const safeTerm = term.replace(/"/g, '');
            return this.ticketService.listAssignmentGroups({
                filter: safeTerm.trim(),
                fields: 'id,name,source',
            });
        };
        this.assignmentGroupHO$ = this.assignmentGroupLoaderHO.pipe(
            switchMap(listAssignmentGroupsByTerm),
        );
        this.assignmentGroupHNO$ = this.assignmentGroupLoaderHNO.pipe(
            switchMap(listAssignmentGroupsByTerm),
        );
        this.assignmentGroupHOFooter$ = this.assignmentGroupLoaderHOFooter.pipe(
            switchMap(listAssignmentGroupsByTerm),
        );
        this.assignmentGroupHNOFooter$ = this.assignmentGroupLoaderHNOFooter.pipe(
            switchMap(listAssignmentGroupsByTerm),
        );
        if (this.scope === 'CELL') {
            this.canEdit = this.entityOwners?.includes(this.userSessionService.getUserSession().id)
                || this.userSessionService.checkRight('cell', Level.ADMIN);
        }
        if (this.scope === 'CUSTOMER') {
            this.canEdit = this.userSessionService.checkRight('company', Level.EDIT);
        }
        if (this.scope === 'PROJECT') {
            this.canEdit = this.userSessionService.checkRight('project', Level.EDIT);
        }
        const assignmentGroupConfigRows = await this.toAggregatedConfigsRows(this.assignmentGroupConfigs);
        this.dataSource = new MatTableDataSource(assignmentGroupConfigRows);
    }

    public async ngOnChanges(): Promise<void> {
        if (this.assignmentGroupConfigs && this.dataSource) {
            this.dataSource.data = await this.toAggregatedConfigsRows(this.assignmentGroupConfigs);
        }
    }

    public async createAssignmentGroupConfig(): Promise<void> {
        const formDataRawValue = this.form.getRawValue();
        const assignmentGroupConfigToCreateHO = this.toAssignmentGroupConfig(formDataRawValue, 'HO');
        const assignmentGroupConfigToCreateHNO = this.toAssignmentGroupConfig(formDataRawValue, 'HNO');
        const assignmentGroupConfigCreatedHO = await this.assignmentGroupConfigService.createAssignmentGroupConfig(
            assignmentGroupConfigToCreateHO
        ).toPromise();
        const assignmentGroupConfigCreatedHNO = await this.assignmentGroupConfigService.createAssignmentGroupConfig(
            assignmentGroupConfigToCreateHNO
        ).toPromise();
        if (!assignmentGroupConfigCreatedHO) {
            return;
        }
        if (!assignmentGroupConfigCreatedHNO) {
            return;
        }
        this.form.patchValue({
            ticketType: '',
            assignmentType: '',
            assignmentGroupHO: '',
            assignmentGroupHNO: '',
        });
        Object.values(this.form.controls).map(control => control.setErrors(null));
        this.dataSource.data = await this.toAggregatedConfigsRows([...this.assignmentGroupConfigs, assignmentGroupConfigCreatedHO, assignmentGroupConfigCreatedHNO]);
        this.assignmentGroupConfigs.push(assignmentGroupConfigCreatedHO);
        this.assignmentGroupConfigs.push(assignmentGroupConfigCreatedHNO);
        return this.endProcess('Groupes d\'affectation ajoutés', 'info');
    }

    public openDeleteDialog(row: AggregatedConfigsRow): void {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Désactivation des groupes d\'affectation',
                content: 'Etes-vous certain de vouloir supprimer cette configuration d\'affectation ?'
            },
            width: '500px',
            autoFocus: false,
        });
        const subscription = dialogRef.afterClosed().subscribe(async action => {
            if (action) {
                return this.deleteAssignmentGroup(row);
            }
            subscription?.unsubscribe();
        });
    }

    public async updateAssignmentGroupConfig(row: AggregatedConfigsRow, event: AssignmentGroup, period: 'HO' | 'HNO'): Promise<void> {
        const currentAssignmentGroupId = period === 'HO' ? row.assignmentGroupObjectHO?.id : row.assignmentGroupObjectHNO?.id;
        if (!event?.id || currentAssignmentGroupId === event.id) {
            return;
        }
        const assignmentGroupConfigId = period === 'HO' ? row.assignmentGroupConfigHOId : row.assignmentGroupConfigHNOId;

        if (assignmentGroupConfigId) {
            const updated = await this.assignmentGroupConfigService.updateAssignmentGroupConfig(
                assignmentGroupConfigId,
                new AssignmentGroupConfig({ assignmentGroup: event.id }),
            ).toPromise();
            if (!updated) {
                return;
            }
            this.dataSource.data = this.dataSource.data.map((d) => {
                if (d.assignmentGroupConfigHOId === updated.id) {
                    d.assignmentGroupHO = event.name;
                    d.assignmentGroupObjectHO = event;
                }
                if (d.assignmentGroupConfigHNOId === updated.id) {
                    d.assignmentGroupHNO = event.name;
                    d.assignmentGroupObjectHNO = event;
                }
                return d;
            });
            const updatedConfig = this.assignmentGroupConfigs.find(({ id }) => id === updated.id);
            if (updatedConfig) {
                updatedConfig.assignmentGroup = updated.assignmentGroup;
            }
            this.endProcess('Groupe d\'affectation modifié', 'info');
        } else {
            const assignmentGroupConfig = new AssignmentGroupConfig({
                assignmentGroup: event.id,
                assignmentType: row.assignmentType,
                scope: row.scope,
                entityId: this.entity,
                ticketType: row.ticketType,
                period
            });
            const assignmentGroupConfigCreated = await this.assignmentGroupConfigService.createAssignmentGroupConfig(assignmentGroupConfig).toPromise();

            if (!assignmentGroupConfigCreated) {
                return;
            }
            this.dataSource.data = await this.toAggregatedConfigsRows([...this.assignmentGroupConfigs, assignmentGroupConfigCreated]);
            this.assignmentGroupConfigs.push(assignmentGroupConfigCreated);
            return this.endProcess('Groupe d\'affectation ajouté', 'info');
        }
    }

    public editRow(row: AggregatedConfigsRow): void {
        if (!row.edit && this.editingRow) {
            this.editingRow.edit = false;
        }
        row.edit = !row.edit;
        if (row.edit) {
            this.editingRow = row;
        }
    }

    private async deleteAssignmentGroup(row: AggregatedConfigsRow): Promise<void> {
        const deleteRequests = [];

        if (row.assignmentGroupConfigHOId) {
            deleteRequests.push(this.assignmentGroupConfigService.deleteAssignmentGroupConfig(row.assignmentGroupConfigHOId).toPromise());
        }
        if (row.assignmentGroupConfigHNOId) {
            deleteRequests.push(this.assignmentGroupConfigService.deleteAssignmentGroupConfig(row.assignmentGroupConfigHNOId).toPromise());
        }

        if (deleteRequests.length > 0) {
            await Promise.all(deleteRequests);
            this.dataSource.data = this.dataSource.data.filter(it => it.id !== row.id);
            this.assignmentGroupConfigs.filter(it => it.id !== row.id);
            return this.endProcess('Configuration d\'affectation supprimée', 'info');
        }
    }

    private toAssignmentGroupConfig(formData: any, period: 'HO' | 'HNO'): AssignmentGroupConfig {
        const baseAssignmentGroup = {
            assignmentType: formData.assignmentType,
            scope: formData.scope,
            entityId: formData.entityId,
            ticketType: formData.ticketType,
        };
        return {
            ...baseAssignmentGroup,
            period,
            assignmentGroup: formData[`assignmentGroup${period}`].id,
        };
    }

    private async toAggregatedConfigsRows(assignmentGroupConfigs: AssignmentGroupConfig[]): Promise<AggregatedConfigsRow[]> {
        const uniqAssignmentGroupIds = [...new Set(assignmentGroupConfigs.map(({ assignmentGroup }) => assignmentGroup))];
        const assignmentGroups = uniqAssignmentGroupIds.length ? await this.ticketService.listAssignmentGroups({
            filter: `id:(${uniqAssignmentGroupIds.join('|')})`,
            fields: 'id,name,source',
        }).toPromise() : [];
        const assignmentGroupsMap = new Map<string, RowAssignmentGroup>(
            assignmentGroups.map((assignmentGroup) => [assignmentGroup.id, assignmentGroup]),
        );

        const aggregatedConfigs = assignmentGroupConfigs.reduce((acc, assignmentGroupConfig) => {
            const aggregateKey = `${assignmentGroupConfig.ticketType}-${assignmentGroupConfig.entityId}-${assignmentGroupConfig.assignmentType}`;
            if (!acc.has(aggregateKey)) {
                const rowFormatted = {
                    id: aggregateKey,
                    ticketType: assignmentGroupConfig.ticketType,
                    entityId: assignmentGroupConfig.entityId,
                    assignmentType: assignmentGroupConfig.assignmentType,
                    edit: false,
                    hover: false,
                    isEditable: assignmentGroupConfig.scope === this.scope,
                    priority: AssignmentGroupConfigScopePriority[assignmentGroupConfig.scope],
                    scope: assignmentGroupConfig.scope,
                };
                acc.set(aggregateKey, rowFormatted);
            }
            const currentAggregatedConfig = acc.get(aggregateKey);
            const assignmentGroup = assignmentGroupsMap.get(assignmentGroupConfig.assignmentGroup);
            currentAggregatedConfig[`assignmentGroup${assignmentGroupConfig.period}`] = assignmentGroup?.name;
            currentAggregatedConfig[`assignmentGroupObject${assignmentGroupConfig.period}`] = assignmentGroup ?? {
                id: assignmentGroupConfig.assignmentGroup,
                name: assignmentGroupConfig.assignmentGroup,
            };
            currentAggregatedConfig[`assignmentGroupConfig${assignmentGroupConfig.period}Id`] = assignmentGroupConfig.id;
            return acc;
        }, new Map<string, AggregatedConfigsRow>());

        const isFirstLine = (assignmentGroupConfig) => assignmentGroupConfig === FIRST_LINE_ASSIGNMENT_TYPE;

        return [...aggregatedConfigs.values()].sort(({ assignmentType: assignmentTypeA, scope: scopeA }, { assignmentType: assignmentTypeB, scope: scopeB }) => {
            return isFirstLine(assignmentTypeA) && !isFirstLine(assignmentTypeB) ? -1 :
                !isFirstLine(assignmentTypeA) && isFirstLine(assignmentTypeB) ? 1 :
                    assignmentTypeA > assignmentTypeB ? 1 :
                        assignmentTypeB > assignmentTypeA ? -1 :
                            scopeA > scopeB ? -1 : 1;
        });
    }

    private endProcess(message: string, type: TYPE_ALERT) {
        this.snackbarService.show({
            message,
            type,
            timeout: 3000,
        });
        this.notificationService.addNotification({
            type,
            title: message,
            description: '',
            code: 0,
        });
    }

    private createForm(): void {
        this.form = this.formBuilder.group({
            ticketType: ['', Validators.required],
            assignmentType: ['', Validators.required],
            assignmentGroupHO: ['', Validators.required],
            assignmentGroupHNO: ['', Validators.required],
            scope: this.scope,
            entityId: this.entity,
        });
    }
}
